/**
 * Zod schemas for admin feature management
 */

import { z } from 'zod';
import { OptionSchemaEntrySchema } from './feature.schemas';

export const ShipSchema = z.object({
  name: z.string(),
  version: z.string(),
  pin: z.object({ file: z.string(), var: z.string() }),
  primary: z.boolean().default(false),
});

export const FeatureSchema = z.object({
  id: z.string(),
  name: z.string(),
  display_name: z.string(),
  description: z.string(),
  source: z.string(),
  version: z.string(),
  default_options: z.record(z.string(), z.unknown()),
  option_schema: z.record(z.string(), OptionSchemaEntrySchema),
  category: z.string(),
  is_gated: z.boolean(),
  gate_attribute: z.string().nullable(),
  gate_value: z.string().nullable(),
  is_active: z.boolean(),
  dependencies: z.array(z.string()).default([]),
  dependencies_note: z.string().nullable().optional(),
  ships: z.array(ShipSchema).nullable().optional(),
  variant_versions: z.record(z.string(), z.string()).nullable().optional(),
  variant_key: z.string().nullable().optional(),
  variants: z.array(z.string()).nullable().optional(),
  default_variant: z.string().nullable().optional(),
  retired_variants: z
    .record(z.string(), z.object({ release: z.string(), package: z.string() }))
    .nullable()
    .optional(),
  variant_end_of_support: z.record(z.string(), z.string()).nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
});

export const FeatureListSchema = z.object({
  features: z.array(FeatureSchema),
});

export const AssignmentSchema = z.object({
  id: z.string(),
  project_id: z.string(),
  project_name: z.string(),
  feature_id: z.string(),
  feature: FeatureSchema.nullable().optional(),
  options: z.record(z.string(), z.unknown()).nullable(),
  created_by: z.string(),
  updated_by: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});

export const AssignmentListSchema = z.object({
  assignments: z.array(AssignmentSchema),
});

export const DependentsResponseSchema = z.object({
  feature_name: z.string(),
  dependents: z.array(z.string()),
});
export type DependentsResponse = z.infer<typeof DependentsResponseSchema>;

export const AuditLogEntrySchema = z.object({
  id: z.string(),
  project_id: z.string(),
  feature_id: z.string(),
  action: z.string(),
  actor: z.string(),
  before_state: z.record(z.string(), z.unknown()).nullable(),
  after_state: z.record(z.string(), z.unknown()).nullable(),
  created_at: z.string(),
});

export const AuditLogListSchema = z.object({
  entries: z.array(AuditLogEntrySchema),
});

export const AssignmentWithDepsResponseSchema = z.object({
  assignment: AssignmentSchema,
  auto_assigned_deps: z.array(z.string()).default([]),
  unresolved_deps: z.array(z.string()).default([]),
});

export type Feature = z.infer<typeof FeatureSchema>;
export type FeatureList = z.infer<typeof FeatureListSchema>;
export type Assignment = z.infer<typeof AssignmentSchema>;
export type AssignmentList = z.infer<typeof AssignmentListSchema>;
export type AssignmentWithDepsResponse = z.infer<typeof AssignmentWithDepsResponseSchema>;
export const SyncResultSchema = z.object({
  created: z.array(z.string()),
  updated: z.array(z.string()),
  unchanged: z.array(z.string()),
  errors: z.array(z.string()),
  // Registry packages not on this deployment's feature allowlist.
  skipped: z.array(z.string()).default([]),
  // Deactivated: their repository left every registry base (#172).
  retired: z.array(z.string()).default([]),
  dependency_changes: z.array(z.object({
    feature: z.string(),
    added_deps: z.array(z.string()),
    affected_projects: z.array(z.string()),
    affected_packs: z.array(z.string()),
  })).default([]),
  configuration_warnings: z.array(z.object({
    feature: z.string(),
    project: z.string(),
    unconfigured_options: z.array(z.string()),
  })).default([]),
  // Language-pack migration (#176): references moved onto lang-* packs, the
  // ones left in place (to fix by hand), and leftover variant rows retired.
  migrated: z.array(z.record(z.string(), z.unknown())).default([]),
  migration_skipped: z.array(z.object({
    from: z.string(),
    to: z.string().nullable(),
    kind: z.string(),
    reason: z.string(),
    line: z.string().nullable().optional(),
  })).default([]),
  deactivated_variants: z.array(z.string()).default([]),
  deactivated_replaced: z.array(z.string()).default([]),
});

export const ImageCacheSchema = z.object({
  id: z.string(),
  feature_hash: z.string(),
  image_tag: z.string(),
  status: z.string(),
  error_message: z.string().nullable(),
  build_job_name: z.string().nullable(),
  features_json: z.record(z.string(), z.unknown()),
  created_at: z.string(),
  last_used_at: z.string(),
});

export const ImageCacheListSchema = z.object({
  images: z.array(ImageCacheSchema),
});

export type ImageCacheEntry = z.infer<typeof ImageCacheSchema>;
export type AuditLogEntry = z.infer<typeof AuditLogEntrySchema>;
export type AuditLogList = z.infer<typeof AuditLogListSchema>;
export type SyncResult = z.infer<typeof SyncResultSchema>;

export const BaseImageInfoSchema = z.object({
  full_ref: z.string(),
  version: z.string().nullable(),
  tag: z.string(),
  digest: z.string().nullable(),
  built_at: z.string().nullable(),
});
export type BaseImageInfo = z.infer<typeof BaseImageInfoSchema>;

// --- Feature Pack Schemas ---

export const PackItemSchema = z.object({
  id: z.string(),
  feature_id: z.string(),
  feature: FeatureSchema.nullable().optional(),
  options: z.record(z.string(), z.unknown()).nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type PackItem = z.infer<typeof PackItemSchema>;

export const PackItemWithDepsResponseSchema = z.object({
  item: PackItemSchema,
  auto_assigned_deps: z.array(z.string()).default([]),
  unresolved_deps: z.array(z.string()).default([]),
});
export type PackItemWithDepsResponse = z.infer<typeof PackItemWithDepsResponseSchema>;

export const LinkedProjectSchema = z.object({
  project_id: z.string(),
  project_name: z.string(),
  pack_id: z.string(),
  linked_by: z.string(),
});
export type LinkedProject = z.infer<typeof LinkedProjectSchema>;

export const FeaturePackSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  display_name: z.string(),
  description: z.string().nullable().optional(),
  item_count: z.number(),
  linked_project_count: z.number(),
  created_by: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type FeaturePackSummary = z.infer<typeof FeaturePackSummarySchema>;

export const FeaturePackDetailSchema = z.object({
  id: z.string(),
  name: z.string(),
  display_name: z.string(),
  description: z.string().nullable().optional(),
  items: z.array(PackItemSchema),
  linked_projects: z.array(LinkedProjectSchema),
  created_by: z.string(),
  updated_by: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type FeaturePackDetail = z.infer<typeof FeaturePackDetailSchema>;

export const FeaturePackListSchema = z.object({
  packs: z.array(FeaturePackSummarySchema),
});

export const RelinkedProjectSchema = z.object({
  project_id: z.string(),
  previous_pack: z.string(),
});

export const BulkLinkResponseSchema = z.object({
  linked: z.array(z.string()),
  already_linked_to_this_pack: z.array(z.string()),
  relinked_from_other_pack: z.array(RelinkedProjectSchema),
  errors: z.array(z.string()),
});
export type BulkLinkResponse = z.infer<typeof BulkLinkResponseSchema>;

// --- Runtime Config ---

export const CertInfoSchema = z.object({
  subject: z.string(),
  issuer: z.string(),
  not_after: z.string(),
  sha256: z.string(),
  expired: z.boolean(),
  expires_soon: z.boolean(),
});
export type CertInfo = z.infer<typeof CertInfoSchema>;

export const CaUploadResponseSchema = z.object({
  bundle: z.string(),
  certificates: z.array(CertInfoSchema),
  added: z.number(),
  already_present: z.number(),
  skipped_not_ca: z.array(z.string()),
  skipped_expired: z.array(z.string()),
});
export type CaUploadResponse = z.infer<typeof CaUploadResponseSchema>;

export const RuntimeConfigEntrySchema = z.object({
  kind: z.enum(['file', 'env']),
  name: z.string(),
  target_path: z.string().nullable(),
  file_mode: z.string().nullable(),
  secret: z.boolean(),
  required: z.boolean(),
  label: z.string(),
  description: z.string(),
  from_slot: z.boolean(),
  is_set: z.boolean(),
  size_bytes: z.number().nullable(),
  updated_at: z.string().nullable(),
  updated_by: z.string().nullable(),
  // Content of a non-secret value (#127); always null for secret entries.
  value: z.string().nullable().optional(),
  // 'pem-ca-bundle' slots are edited as a certificate list.
  format: z.enum(['pem-ca-bundle']).nullable().optional(),
  certificates: z.array(CertInfoSchema).nullable().optional(),
});

export const RuntimeConfigListSchema = z.object({
  feature_id: z.string(),
  entries: z.array(RuntimeConfigEntrySchema),
  owned_scope: z.enum(['global', 'pack', 'project']),
  owned_by_context: z.boolean(),
});

export type RuntimeConfigEntry = z.infer<typeof RuntimeConfigEntrySchema>;
export type RuntimeConfigList = z.infer<typeof RuntimeConfigListSchema>;

// --- Missing required runtime config (pack/project alert) ---

export const MissingRuntimeConfigItemSchema = z.object({
  feature_id: z.string(),
  feature_name: z.string(),
  display_name: z.string(),
  missing: z.array(z.string()),
  owned_scope: z.enum(['global', 'pack', 'project']),
  gate_value: z.string().nullable(),
  source: z.enum(['pack', 'standalone']).nullable(),
});

export const MissingRuntimeConfigListSchema = z.object({
  items: z.array(MissingRuntimeConfigItemSchema),
});

export type MissingRuntimeConfigItem = z.infer<typeof MissingRuntimeConfigItemSchema>;
